<template>
  <ValidationProvider
    name="Lead Underwriter"
    rules="required"
    v-slot="{ errors }"
  >
    <div class="underwriter-dropdown">
      <AdvancedDropdown
        v-model="modelValue"
        :options="options"
        :searchable="searchable"
        :can-add-new="false"
        :disabled="disabled"
        size="sm"
        single
        :class="{ 'is-invalid': !!errors.length }"
      >
        <template v-slot:label>
          <span>Lead Underwriter <span class="text-danger">*</span></span>
        </template>
        <template v-slot:button-content>
          <template v-if="selectedOption">
            {{ selectedOption.text }}
          </template>
          <template v-else>Select Lead Underwriter</template>
        </template>

        <template v-slot:item-extra="slotData">
          <SyndicateLinesInput
            v-if="slotData && isSelected(slotData.value) && slotData.value !== KEEP_AS_IS_VALUE"
            :value="selectedSyndicates"
            @input="(value) => onSyndicatesInput(value)"
          />
        </template>
      </AdvancedDropdown>
      <div v-if="!!errors.length" class="invalid-feedback d-block">
        {{ errors[0] }}
      </div>
    </div>
  </ValidationProvider>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'

import { useRLSGroup } from '@composables'
import { AdvancedDropdown } from '@components/advanced'
import { SyndicateLinesInput } from '../rls-syndicate-lines-input'
import { ValidationProvider } from 'vee-validate'
import { watchEffect } from '@vue/composition-api'
import { KEEP_AS_IS_OPTION, KEEP_AS_IS_VALUE } from '@constants'

export default defineComponent({
  name: 'RLSUnderwriterLeadsChecklist',
  components: {
    SyndicateLinesInput,
    AdvancedDropdown,
    ValidationProvider
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    isFetching: {
      type: Boolean,
      default: false
    },
    rlsGroupItems: {
      type: Array,
      default: () => []
    },
    autoFetchRlsGroupItems: {
      type: Boolean,
      default: true
    },
    searchable: {
      type: Boolean,
      default: true
    },
    useKeepAsIsOption: Boolean,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input'],
  setup(props, { emit }) {
    const { isFetching: innerFetching, rlsGroupItems } = useRLSGroup(
      'Underwriter',
      props.autoFetchRlsGroupItems
    )

    const modelValue = computed({
      get: () => props.value.map((item) => item.id),
      set: (newValue) => {
        const selectedId = newValue[0] || null
        if (!selectedId) {
          emit('input', [])
          return
        }

        // Same underwriter may already be selected as a follower (a market
        // leading on one syndicate, following on another) - carry its
        // syndicate lines over rather than discarding them, since selecting
        // this dropdown is a fresh choice of WHO leads, not an edit to what
        // either bucket's syndicates already hold.
        const existingItem = props.value.find((item) => item.id === selectedId)
        emit('input', [{
          id: selectedId,
          syndicates: existingItem ? existingItem.syndicates : []
        }])
      }
    })

    // Every company type can see every underwriter here - access is
    // controlled by disabling the whole control once the record already has
    // a Lead Underwriter (see BDXEditForm's isLeadUnderwriterDisabled /
    // isFollowUnderwriterAssignmentDisabled), not by filtering who appears
    // in this list.
    //
    // Also no longer filtered by disabledValues: the same underwriter can
    // now be selected here AND in the follower list at once (leading on one
    // syndicate, following on another) - see GlobalUMRUnderwriter's own
    // docstring on the backend for why this is a real business case, not a
    // data error.
    const options = computed(() => {
      let options = rlsGroupItems.value;
      if (props.useKeepAsIsOption) {
        options = [{
          id: KEEP_AS_IS_VALUE,
          underwriter: KEEP_AS_IS_OPTION.text
        }, ...options];
      }
      options = options
        .map((rlsGroupItem) => {
          const item = props.value.find((item) => item.id === rlsGroupItem.id)
          const syndicates = (item && item.syndicates) || []
          const total = syndicates.reduce((sum, s) => {
            const pct = Number(s.slip_share_pct)
            return sum + (Number.isFinite(pct) ? pct : 0)
          }, 0)

          let text = rlsGroupItem.underwriter
          if (total > 0) {
            // Floating-point addition of two 9dp values can land a few
            // units past the 9th decimal (e.g. 60.123456789 + 39.87654321
            // -> 99.99999999900001) - round to the precision the rest of
            // the platform shows before display, not raw IEEE754 noise.
            // Trailing zeros then come off, same as everywhere else a share
            // is shown - a plain 40% share reads 40%, not 40.000000000%.
            text += ` (${total.toFixed(9).replace(/\.?0+$/, '')}%)`
          }

          return {
            text: text,
            value: rlsGroupItem.id
          }
        })
        return options
    })

    const selectedOption = computed(() => {
      if (modelValue.value.length === 0) return null
      return options.value.find((opt) => opt.value === modelValue.value[0])
    })

    const selectedSyndicates = computed(() => {
      if (props.value.length === 0) return []
      return props.value[0].syndicates || []
    })

    const isSelected = (value) => {
      return modelValue.value.includes(value)
    }

    const onSyndicatesInput = (syndicates) => {
      emit('input', [{ id: modelValue.value[0], syndicates }])
    }

    watchEffect(() => {
      if (!props.autoFetchRlsGroupItems) {
        innerFetching.value = props.isFetching
        rlsGroupItems.value = props.rlsGroupItems
      }
    })

    return {
      modelValue,
      options,
      selectedOption,
      selectedSyndicates,
      isSelected,
      onSyndicatesInput,
      innerFetching,
      KEEP_AS_IS_VALUE,

    }
  }
})
</script>

<style scoped>
.underwriter-dropdown {
  position: relative;
}

.underwriter-dropdown >>> .advanced-dropdown .dropdown-menu .list-group-item {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  min-height: 36px;
}

.underwriter-dropdown >>> .advanced-dropdown .dropdown-menu {
  width: min(430px, calc(100vw - 32px));
}

.underwriter-dropdown
  >>> .advanced-dropdown
  .dropdown-menu
  .advanced-dropdown-item-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.underwriter-dropdown
  >>> .advanced-dropdown
  .dropdown-menu
  .list-group-item
  .item-extra {
  flex: 0 0 auto;
  margin-left: 12px;
}
</style>
